import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import './Pricing.css';

function Pricing() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [searchParams] = useSearchParams();
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();

    // Check for subscription status in URL
    const subscriptionStatus = searchParams.get('subscription');

    useEffect(() => {
        fetchPricing();
    }, []);

    const fetchPricing = async () => {
        try {
            const response = await fetch(`${API_URL}/api/stripe/pricing`);
            const data = await response.json();
            if (data.success) {
                setPlans(data.plans);
            }
        } catch (error) {
            console.error('Error fetching pricing:', error);
        }
        setLoading(false);
    };

    const handleSubscribe = async (planId) => {
        if (!currentUser) {
            navigate('/auth', { state: { returnTo: '/pricing' } });
            return;
        }

        if (planId === 'free') {
            navigate('/home');
            return;
        }

        setSubscribing(true);

        try {
            const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    email: currentUser.email,
                    successUrl: `${window.location.origin}/home?subscription=success`,
                    cancelUrl: `${window.location.origin}/pricing?subscription=cancelled`
                })
            });

            const data = await response.json();

            if (data.success && data.url) {
                // Redirect to Stripe Checkout
                window.location.href = data.url;
            } else {
                alert('Failed to start checkout. Please try again.');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Failed to start checkout. Please try again.');
        }

        setSubscribing(false);
    };

    const currentPlan = userData?.subscription?.plan || 'free';

    if (loading) {
        return (
            <div className="pricing-container">
                <div className="pricing-loading">
                    <div className="spinner"></div>
                    <p>Loading pricing...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pricing-container">
            {/* Status Messages */}
            {subscriptionStatus === 'success' && (
                <div className="pricing-alert success">
                    <span className="alert-icon">🎉</span>
                    <span>Welcome to Premium! Your subscription is now active.</span>
                </div>
            )}
            {subscriptionStatus === 'cancelled' && (
                <div className="pricing-alert info">
                    <span className="alert-icon">ℹ️</span>
                    <span>Checkout was cancelled. You can try again anytime.</span>
                </div>
            )}

            {/* Header */}
            <div className="pricing-header">
                <h1>Choose Your Plan</h1>
                <p>Start learning for free or go unlimited with Premium</p>
            </div>

            {/* Plans */}
            <div className="pricing-plans">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`pricing-card ${plan.recommended ? 'recommended' : ''} ${currentPlan === plan.id ? 'current' : ''}`}
                    >
                        {plan.recommended && (
                            <div className="recommended-badge">Most Popular</div>
                        )}
                        {currentPlan === plan.id && (
                            <div className="current-badge">Current Plan</div>
                        )}

                        <div className="plan-header">
                            <h2 className="plan-name">{plan.name}</h2>
                            <div className="plan-price">
                                {plan.price === 0 ? (
                                    <span className="price-free">Free</span>
                                ) : (
                                    <>
                                        <span className="price-amount">${plan.price}</span>
                                        <span className="price-interval">/{plan.interval}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <ul className="plan-features">
                            {plan.features.map((feature, idx) => (
                                <li key={idx}>
                                    <span className="feature-check">✓</span>
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>

                        <button
                            className={`plan-button ${plan.recommended ? 'primary' : 'secondary'}`}
                            onClick={() => handleSubscribe(plan.id)}
                            disabled={subscribing || currentPlan === plan.id}
                        >
                            {currentPlan === plan.id
                                ? 'Current Plan'
                                : plan.price === 0
                                    ? 'Get Started'
                                    : subscribing
                                        ? 'Loading...'
                                        : 'Subscribe Now'
                            }
                        </button>
                    </div>
                ))}
            </div>

            {/* FAQ */}
            <div className="pricing-faq">
                <h3>Frequently Asked Questions</h3>

                <div className="faq-item">
                    <h4>Can I cancel anytime?</h4>
                    <p>Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
                </div>

                <div className="faq-item">
                    <h4>What happens to my progress if I cancel?</h4>
                    <p>Your progress is saved forever. If you resubscribe later, all your learning history will still be there.</p>
                </div>

                <div className="faq-item">
                    <h4>Is there a free trial?</h4>
                    <p>The Free plan lets you try LearnViva with up to 3 topics. No credit card required to get started!</p>
                </div>
            </div>

            {/* Back Button */}
            <button className="back-button" onClick={() => navigate(-1)}>
                ← Back
            </button>
        </div>
    );
}

export default Pricing;
